import { useState, useEffect } from "react";

import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { DynamicIcon } from "@/components/skills-marketplace/dynamic-icon";
import { MarkdownViewer } from "@/components/skills-marketplace/markdown-viewer";
import { skillsMarketplaceApi } from "@/lib/api/skills-marketplace";
import type { CustomSkillItem } from "@optidev/shared-types";

export default function CustomSkillDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [skill, setSkill] = useState<CustomSkillItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    skillsMarketplaceApi
      .getCustomSkill(id)
      .then(setSkill)
      .catch(() => {
        toast.error("Skill not found");
        navigate("/dashboard/skills");
      })
      .finally(() => setIsLoading(false));
  }, [id, navigate]);

  const handleDelete = async (): Promise<void> => {
    if (!id) return;
    try {
      await skillsMarketplaceApi.deleteCustomSkill(id);
      toast.success("Skill deleted");
      navigate("/dashboard/skills");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to delete skill";
      toast.error(message);
    }
  };

  if (isLoading || !skill) {
    return (
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-12 overflow-x-hidden">
      {/* Back button */}
      <button
        onClick={() => navigate("/dashboard/skills")}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Skills
      </button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start gap-4 mb-6">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-violet-100 dark:bg-violet-900/30">
            <DynamicIcon name={skill.icon} className="h-6 w-6 text-violet-600 dark:text-violet-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold">{skill.name}</h1>
            <div className="flex items-center flex-wrap gap-x-2 sm:gap-x-4 gap-y-1 mt-1.5 text-sm text-muted-foreground">
              <span>Created by you</span>
              <span className="px-2 py-0.5 rounded-full text-xs bg-violet-500/10 text-violet-600 dark:text-violet-400 border border-violet-500/20">
                Custom
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-row items-center gap-2 w-full sm:w-auto sm:shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 sm:flex-none"
            onClick={() => navigate(`/dashboard/skills/custom/${id}/edit`)}
          >
            <Pencil className="h-3.5 w-3.5 mr-1.5" />
            Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" className="flex-1 sm:flex-none">
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Custom Skill</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete &ldquo;{skill.name}&rdquo;? This action cannot be
                  undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* SKILL.md viewer */}
      <MarkdownViewer content={skill.content} />
    </div>
  );
}
